import {
  DesktopArtifactArch,
  DesktopArtifactPlatform,
  DesktopDistribution,
} from "@packages/global";
import type { DesktopDownloadTarget } from "../../services/desktop-release.api";
import type { DesktopDownloadOption } from "./types";

export function detectDownloadTarget(): DesktopDownloadTarget {
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent.toLowerCase();
  const isMac = userAgent.includes("mac os") || userAgent.includes("macintosh");
  const isArm = userAgent.includes("arm") || userAgent.includes("aarch64");

  if (isMac) {
    return {
      platform: DesktopArtifactPlatform.MACOS,
      arch: DesktopArtifactArch.UNIVERSAL,
      distribution: DesktopDistribution.INSTALLER,
    };
  }

  return {
    platform: DesktopArtifactPlatform.WINDOWS,
    arch: isArm ? DesktopArtifactArch.ARM64 : DesktopArtifactArch.X64,
    distribution: DesktopDistribution.INSTALLER,
  };
}

export function pickDownloadOption(
  options: DesktopDownloadOption[],
  selectedTarget: DesktopDownloadTarget,
): DesktopDownloadOption | null {
  return options.find((option) => sameTarget(option.target, selectedTarget))
    ?? options.find((option) =>
      option.target.platform === selectedTarget.platform
      && option.target.distribution === selectedTarget.distribution,
    )
    ?? options.find((option) => option.target.platform === selectedTarget.platform)
    ?? options[0]
    ?? null;
}

export function dedupeDownloadOptions(options: DesktopDownloadOption[]): DesktopDownloadOption[] {
  const seen = new Set<string>();
  return [...options]
    .sort((left, right) => compareVersion(right.manifest.version, left.manifest.version))
    .filter((option) => {
      if (seen.has(option.manifest.artifact.id)) return false;
      seen.add(option.manifest.artifact.id);
      return true;
    });
}

export function sameTarget(left: DesktopDownloadTarget, right: DesktopDownloadTarget): boolean {
  return left.platform === right.platform
    && left.arch === right.arch
    && left.distribution === right.distribution;
}

export function uniqueValues<T extends string>(values: T[]): T[] {
  return Array.from(new Set(values));
}

export function pickTargetForPlatform(
  options: DesktopDownloadOption[],
  platform: DesktopArtifactPlatform,
  current: DesktopDownloadTarget,
): DesktopDownloadTarget {
  return options.find((option) =>
    option.target.platform === platform
    && option.target.arch === current.arch
    && option.target.distribution === current.distribution,
  )?.target
    ?? options.find((option) =>
      option.target.platform === platform
      && option.target.distribution === current.distribution,
    )?.target
    ?? options.find((option) => option.target.platform === platform)?.target
    ?? current;
}

export function pickTargetForArch(
  options: DesktopDownloadOption[],
  platform: DesktopArtifactPlatform,
  arch: DesktopArtifactArch,
  current: DesktopDownloadTarget,
): DesktopDownloadTarget {
  return options.find((option) =>
    option.target.platform === platform
    && option.target.arch === arch
    && option.target.distribution === current.distribution,
  )?.target
    ?? options.find((option) =>
      option.target.platform === platform
      && option.target.arch === arch,
    )?.target
    ?? current;
}

export function pickTargetForDistribution(
  options: DesktopDownloadOption[],
  platform: DesktopArtifactPlatform,
  arch: DesktopArtifactArch,
  distribution: DesktopDistribution,
): DesktopDownloadTarget {
  return options.find((option) =>
    option.target.platform === platform
    && option.target.arch === arch
    && option.target.distribution === distribution,
  )?.target
    ?? options.find((option) =>
      option.target.platform === platform
      && option.target.distribution === distribution,
    )?.target
    ?? options.find((option) => option.target.distribution === distribution)?.target
    ?? options[0]?.target
    ?? { platform, arch, distribution };
}

export function targetLabel(target: DesktopDownloadTarget, t: (key: string) => string): string {
  return [
    platformLabel(target.platform, t),
    archLabel(target.arch, t),
    distributionLabel(target.distribution, t),
  ].join(" · ");
}

export function platformLabel(platform: DesktopArtifactPlatform, t: (key: string) => string): string {
  return platform === DesktopArtifactPlatform.MACOS
    ? t("home.platformMacos")
    : t("home.platformWindows");
}

export function archLabel(arch: DesktopArtifactArch, t: (key: string) => string): string {
  if (arch === DesktopArtifactArch.ARM64) return t("home.archArm64");
  if (arch === DesktopArtifactArch.UNIVERSAL) return t("home.archUniversal");
  return t("home.archX64");
}

export function distributionLabel(distribution: DesktopDistribution, t: (key: string) => string): string {
  return distribution === DesktopDistribution.PORTABLE
    ? t("home.distributionPortable")
    : t("home.distributionInstaller");
}

export function formatSize(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function formatDate(value: Date | string, locale?: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

function compareVersion(left: string, right: string): number {
  const leftParts = left.split(".").map(Number);
  const rightParts = right.split(".").map(Number);
  for (let index = 0; index < 3; index += 1) {
    const difference = (leftParts[index] ?? 0) - (rightParts[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}
